import { alpha, Box, Grid, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toJpeg } from 'html-to-image';
import Image from '../../fl-components/Image';
import ClassBannerPreview from './ClassBannerPreview';
import FirstBanner from '../../image/Sample-Banner-Bottom-Image.png';
import palette from '../../theme/palette';
import { UploadSingleFile } from '../../fl-components/upload';
import Iconify from '../../fl-components/Iconify';
import { ConvertToBase64 } from '../../helper/commonHelper';

export default function ClassBannerSelect({
  className,
  onImageUpdate,
  setBase64Generation,
  base64Generating,
  isDiscoverable
}) {
  // Assets array
  const assetsArray = useSelector((state) => state.bannerAssets.assetsArray);
  // Selected image
  const [selectedImage, setSelectedImage] = useState(0);
  // Current User
  const currentUser = useSelector((state) => state.auth.currentUser);
  // Custom banner
  const [selectedFile, setSelectedFile] = useState(null);
  // Assets url
  const [assetUrl, setAssetUrl] = useState('');
  // Selected asset index
  const [assetIndex, setAssetIndex] = useState(0);

  // Handle drop event while image is selected
  const handleDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];

    setSelectedFile(
      Object.assign(file, {
        preview: URL.createObjectURL(file)
      })
    );
    setSelectedImage(2);
  });

  useEffect(() => {
    if (assetsArray !== null) setAssetUrl(`https://cms.fainlearn.com${assetsArray[0].url}`);
  }, []);

  useEffect(() => {
    const generateBase64URL = async () => {
      setBase64Generation(true);
      // If this index is 2 then user need to upload custom banner.
      if (selectedImage === 2) {
        const base64 = await ConvertToBase64(selectedFile);
        const imageObj = {
          imageBaseURL: base64,
          name: selectedFile.name,
          type: selectedFile.type
        };
        setBase64Generation(false);
        onImageUpdate(imageObj);
      } else {
        toJpeg(document.getElementById('image-container'), {
          quality: 1,
          width: 1200,
          height: 768,
          canvasHeight: 768,
          canvasWidth: 1200,
          skipAutoScale: true,
          backgroundColor: 'white'
        })
          .then((dataUrl) => {
            const imageObj = {
              // extracting
              imageBaseURL: dataUrl.split('base64,')[1],
              name: `${className}.jpeg`,
              type: 'image/jpeg'
            };
            setBase64Generation(false);
            onImageUpdate(imageObj);
          })
          .catch(() => {
            setBase64Generation(false);
          });
      }
    };

    generateBase64URL();
  }, [assetUrl, selectedImage]);

  return (
    <Box height="100%">
      <Box width={1200} height={768} style={{ display: 'none' }}>
        <Box id="image-container">
          <ClassBannerPreview
            selectedImage={selectedImage}
            imageDetails={{ className, teacherName: currentUser.fullName, assetUrl }}
          />
        </Box>
      </Box>
      <Box
        maxHeight={292}
        height={292}
        style={{ position: 'sticky', top: 0, background: 'white', paddingBottom: 10, zIndex: 999 }}
      >
        {base64Generating && (
          <Skeleton
            width="100%"
            height="100%"
            variant="rectangular"
            sx={{ position: 'absolute', top: 0, left: 0, zIndex: 20 }}
            animation="wave"
          />
        )}
        {/* Custom image is selected */}
        {selectedImage === 2 ? (
          <Image
            src={selectedFile.preview}
            sx={{ width: '100%', height: '100%' }}
            imageStyle={{ objectFit: 'fill' }}
          />
        ) : (
          <ClassBannerPreview
            selectedImage={selectedImage}
            isDiscoverable={isDiscoverable}
            imageDetails={{ className, teacherName: currentUser.fullName, assetUrl }}
            imgStyle={{
              objectFit: 'cover'
            }}
          />
        )}
      </Box>

      <Box mt={3}>
        <Typography fontWeight="bold">Choose Banner:</Typography>
        <Stack
          direction={{
            sm: 'row',
            xs: 'column'
          }}
          alignItems="center"
          mt={1}
          spacing={1}
        >
          <Image
            src={FirstBanner}
            sx={{
              width: '170px',
              border: selectedImage === 0 ? `1px solid ${palette.secondary.main}` : 'none',
              borderRadius: 1,
              cursor: 'pointer'
            }}
            onClick={() => setSelectedImage(0)}
          />
          {selectedFile ? (
            <Box position="relative">
              <Image
                src={selectedFile.preview}
                sx={{
                  width: '170px',
                  height: '100px',
                  border: selectedImage === 2 ? `1px solid ${palette.secondary.main}` : 'none',
                  borderRadius: 1,
                  cursor: 'pointer'
                }}
                onClick={() => setSelectedImage(2)}
              />
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedFile(null);
                  setSelectedImage(0);
                }}
                sx={{
                  top: 6,
                  p: '2px',
                  right: 6,
                  position: 'absolute',
                  color: 'common.white',
                  bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                  '&:hover': {
                    bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48)
                  }
                }}
              >
                <Iconify icon="eva:close-fill" />
              </IconButton>
            </Box>
          ) : (
            <UploadSingleFile
              file={selectedFile}
              sx={{ width: '170px', height: '100px', display: 'flex', alignItems: 'center' }}
              accept={{ 'image/*': ['.jpg', '.png'] }}
              onDrop={handleDrop}
              displayAddIcon
              dropZoneCss={{
                width: '100%',
                height: '100px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          )}
        </Stack>
      </Box>

      {assetsArray.length && (
        <Box mt={3}>
          <Typography fontWeight="bold">Assets you may like:</Typography>
          <Grid container spacing={2} mt={1} ml={0}>
            {assetsArray.map((as, index) => (
              <Grid item key={as._id} sm={4} xs={6}>
                <Image
                  src={`https://cms.fainlearn.com${as.url}`}
                  alt={as.name}
                  sx={{
                    width: '150px',
                    height: '150px',
                    border:
                      assetIndex === index
                        ? `1px solid ${palette.secondary.main}`
                        : '1px solid rgba(145, 158, 171, 0.32)',
                    cursor: 'pointer',
                    borderRadius: 1,
                    p: 1
                  }}
                  imageStyle={{ objectFit: 'fill' }}
                  onClick={() => {
                    setAssetUrl(`https://cms.fainlearn.com${as.url}`);
                    setAssetIndex(index);
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
}

ClassBannerSelect.propTypes = {
  className: PropTypes.string,
  onImageUpdate: PropTypes.func,
  setBase64Generation: PropTypes.func,
  base64Generating: PropTypes.bool,
  isDiscoverable: PropTypes.bool
};
